/**
 * AlertBanner — displays active (unacknowledged) alerts with actions.
 *
 * Renders a list of alert rows. Each row shows the alert message with
 * Acknowledge and Dismiss action buttons. Mutation errors are shown
 * inline next to the failed button (Pattern 8.3 / NFR-RESIL-FE-03).
 *
 * Returns null when the alerts list is empty (no DOM output).
 */
import type { Alert } from '@/types';
import styles from './AlertBanner.module.css';

export interface AlertBannerProps {
  alerts: Alert[];
  onAcknowledge: (alertId: number) => void;
  onDismiss: (alertId: number) => void;
  /** IDs of alerts whose acknowledge action is currently pending */
  acknowledgePendingIds?: Set<number>;
  /** IDs of alerts whose dismiss action is currently pending */
  dismissPendingIds?: Set<number>;
  /** IDs of alerts whose acknowledge action failed */
  acknowledgeErrorIds?: Set<number>;
  /** IDs of alerts whose dismiss action failed */
  dismissErrorIds?: Set<number>;
}

const ALERT_TYPE_LABELS: Record<string, string> = {
  EC2_INSTANCE_DOWN:       'EC2 Down',
  HEALTH_ENDPOINT_UNHEALTHY: 'Health Check Failed',
  RDS_UNAVAILABLE:         'RDS Unavailable',
  ECS_SERVICE_DEGRADED:    'ECS Degraded',
};

export function AlertBanner({
  alerts,
  onAcknowledge,
  onDismiss,
  acknowledgePendingIds = new Set(),
  dismissPendingIds = new Set(),
  acknowledgeErrorIds = new Set(),
  dismissErrorIds = new Set(),
}: AlertBannerProps) {
  const unacknowledged = alerts.filter((a) => !a.acknowledged);
  if (unacknowledged.length === 0) return null;

  return (
    <section className={styles.container} aria-label="Active alerts">
      <h2 className={styles.heading}>
        ⚠ {unacknowledged.length} Active Alert
        {unacknowledged.length !== 1 ? 's' : ''}
      </h2>
      <ul className={styles.list} role="list">
        {unacknowledged.map((alert) => {
          const ackPending = acknowledgePendingIds.has(alert.id);
          const disPending = dismissPendingIds.has(alert.id);
          const ackError = acknowledgeErrorIds.has(alert.id);
          const disError = dismissErrorIds.has(alert.id);

          return (
            <li key={alert.id} className={styles.alertRow}>
              <div className={styles.alertContent}>
                <span className={styles.alertType}>
                  {ALERT_TYPE_LABELS[alert.alertType] ?? alert.alertType}
                </span>
                <span className={styles.alertMessage}>{alert.message}</span>
                <span className={styles.alertMeta}>
                  {alert.resourceType} · {alert.resourceId} ·{' '}
                  {new Date(alert.triggeredAt).toLocaleString()}
                </span>
              </div>
              <div className={styles.alertActions}>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => onAcknowledge(alert.id)}
                  disabled={ackPending || disPending}
                  aria-label={`Acknowledge alert for ${alert.resourceId}`}
                >
                  {ackPending ? '…' : 'Acknowledge'}
                </button>
                {ackError && (
                  <span role="alert" className={styles.actionError}>
                    Failed to acknowledge
                  </span>
                )}
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.dismissButton}`}
                  onClick={() => onDismiss(alert.id)}
                  disabled={ackPending || disPending}
                  aria-label={`Dismiss alert for ${alert.resourceId}`}
                >
                  {disPending ? '…' : '×'}
                </button>
                {disError && (
                  <span role="alert" className={styles.actionError}>
                    Failed to dismiss
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
